import React, { useState } from 'react';
import useDataFetching from '../../hooks/useDataFetching';
import LoadingState from '../shared/LoadingState';
import ErrorState from '../shared/ErrorState';
import EmptyState from '../shared/EmptyState';
import ModelStudioStats from './ModelStudioStats';
import RAGAgentsList from './RAGAgentsList';
import NewRAGAgentModal from './NewRAGAgentModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const getModelStudioStats = async () => {
  const response = await fetch(`${BACKEND_URL}/api/model-studio/stats`);
  if (!response.ok) {
    throw new Error('Failed to fetch model studio stats');
  }
  return response.json();
};

const getRAGAgents = async () => {
  const response = await fetch(`${BACKEND_URL}/api/rag-agents`);
  if (!response.ok) {
    throw new Error('Failed to fetch RAG agents');
  }
  return response.json();
};

const ModelStudio = () => {
  const [activeTab, setActiveTab] = useState('ml-models');
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch stats (no auto-refresh)
  const { data: stats, loading, error, refetch } = useDataFetching(getModelStudioStats, []);
  
  // Fetch RAG agents (no auto-refresh)
  const { data: ragAgents, loading: ragLoading, refetch: refetchAgents } = useDataFetching(
    getRAGAgents, 
    []
  );

  const handleCreateAgent = async (formData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/rag-agents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setIsModalOpen(false);
        refetchAgents(); // Refresh the list
      }
    } catch (err) {
      console.error('Error creating agent:', err);
    }
  };

  if (loading && !stats) {
    return <LoadingState message="Loading Model Studio..." fullScreen />;
  }

  if (error && !stats) {
    return <ErrorState message={error} onRetry={refetch} fullScreen />;
  }

  const tabs = [
    { id: 'ml-models', label: 'ML Models' },
    { id: 'rag-agents', label: 'RAG Agents' },
    { id: 'knowledge-bases', label: 'Knowledge Bases' }
  ];

  return (
    <div className="min-h-full bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Model Studio</h1>
            <p className="text-gray-600 mt-1">Train, version, and deploy ML models on new data streams</p>
          </div>
          <div className="flex gap-3">
            <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium">
              + New Training Job
            </button>
            <button 
              onClick={() => setIsModalOpen(true)}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
            >
              + New RAG Agent
            </button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
              + New Knowledge Base
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  pb-4 px-1 border-b-2 font-medium text-sm transition-colors
                  ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Stats Cards */}
        <ModelStudioStats stats={stats} />

        {/* Content based on active tab */}
        {activeTab === 'ml-models' && (
          <div className="space-y-6">
            {/* Training Jobs Section */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Training Jobs</h2>
              <EmptyState
                title="No training jobs yet"
                description="Create one to get started!"
                icon={() => <span className="text-5xl">🧠</span>}
              />
            </div>

            {/* Model Versions Section */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Model Versions</h2>
              <EmptyState
                title="No trained models yet"
                description="Complete a training job first!"
                icon={() => <span className="text-5xl">📈</span>}
                actionLabel="Deploy Model"
              />
            </div>
          </div>
        )}

        {activeTab === 'rag-agents' && (
          <div className="space-y-4">
            {ragLoading ? (
              <LoadingState message="Loading RAG agents..." />
            ) : ragAgents && ragAgents.length > 0 ? (
              <RAGAgentsList agents={ragAgents} onEdit={(agent) => console.log('Edit:', agent)} />
            ) : (
              <div className="bg-white rounded-lg shadow p-6">
                <EmptyState
                  title="No RAG Agents"
                  description="Create your first RAG agent to get started"
                  icon={() => <span className="text-5xl">🤖</span>}
                  action={() => setIsModalOpen(true)}
                  actionLabel="Create RAG Agent"
                />
              </div>
            )}
          </div>
        )}

        {activeTab === 'knowledge-bases' && (
          <div className="bg-white rounded-lg shadow p-6">
            <EmptyState
              title="No Knowledge Bases"
              description="Create a knowledge base to power your RAG agents"
              icon={() => <span className="text-5xl">📚</span>}
              actionLabel="Create Knowledge Base"
            />
          </div>
        )}
      </div>

      {/* New RAG Agent Modal */}
      <NewRAGAgentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreateAgent}
      />
    </div>
  );
};

export default ModelStudio;
